import { useMemo } from 'react'
import PropTypes from 'prop-types'
import ReactECharts from 'echarts-for-react'

/**
 * EnrollmentTrendsChart (Bare) - Combined enrollment trends
 * Shows primary and/or secondary enrollment over time
 * For embedding in narrative HTML templates
 */
function EnrollmentTrendsChart({ data }) {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null

    // Aggregate data by year and gender
    const aggregatedData = {}

    data.forEach(record => {
      const year = record.year
      const gender = record.gender
      const total = parseFloat(record.total) || 0

      if (!aggregatedData[year]) {
        aggregatedData[year] = { Boys: 0, Girls: 0 }
      }

      aggregatedData[year][gender] = (aggregatedData[year][gender] || 0) + total
    })

    // Convert to array format for ECharts
    const years = Object.keys(aggregatedData).sort()
    const boysData = years.map(year => aggregatedData[year].Boys || 0)
    const girlsData = years.map(year => aggregatedData[year].Girls || 0)
    const totalData = years.map(
      year =>
        (aggregatedData[year].Boys || 0) + (aggregatedData[year].Girls || 0)
    )

    return { years, boysData, girlsData, totalData }
  }, [data])

  const option = useMemo(() => {
    if (!chartData) return null

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
        },
        formatter: function (params) {
          let content = `<strong>${params[0].axisValue}</strong><br/>`
          params.forEach(param => {
            content += `${param.seriesName}: ${param.value.toLocaleString()}<br/>`
          })
          return content
        },
      },
      legend: {
        bottom: 10,
        itemGap: 20,
      },
      grid: {
        left: 60,
        right: 30,
        top: 30,
        bottom: 60,
      },
      xAxis: {
        type: 'category',
        data: chartData.years,
        axisLine: { lineStyle: { color: '#e5e7eb' } },
        axisLabel: { color: '#6b7280' },
      },
      yAxis: {
        type: 'value',
        axisLine: { lineStyle: { color: '#e5e7eb' } },
        axisLabel: {
          color: '#6b7280',
          formatter: value => value.toLocaleString(),
        },
        splitLine: { lineStyle: { color: '#f3f4f6' } },
      },
      series: [
        {
          name: 'Boys',
          data: chartData.boysData,
          type: 'line',
          smooth: true,
          itemStyle: { color: '#3B82F6' },
          lineStyle: { width: 3 },
        },
        {
          name: 'Girls',
          data: chartData.girlsData,
          type: 'line',
          smooth: true,
          itemStyle: { color: '#EC4899' },
          lineStyle: { width: 3 },
        },
        {
          name: 'Total',
          data: chartData.totalData,
          type: 'line',
          smooth: true,
          itemStyle: { color: '#10B981' },
          lineStyle: { width: 3, type: 'dashed' },
        },
      ],
    }
  }, [chartData])

  if (!data || !chartData) {
    return (
      <div className="text-center py-8 text-gray-500">
        No enrollment trend data available
      </div>
    )
  }

  return <ReactECharts option={option} style={{ height: '400px' }} />
}

EnrollmentTrendsChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      year: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      gender: PropTypes.string.isRequired,
      total: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    })
  ),
}

export default EnrollmentTrendsChart
